import React, { useState, useEffect } from 'react';
import { 
  useGetBlockStatusQuery, 
  useToggleBlockUserMutation 
} from '../../features/block/blockApi';
import { 
  FiSearch, 
  FiRefreshCw, 
  FiUser, 
  FiAlertCircle, 
  FiX, 
  FiCheck, 
  FiChevronLeft, 
  FiChevronRight,
  FiFilter,
  FiXCircle
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const UserBlockManagement = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [blockFilter, setBlockFilter] = useState('');
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [reason, setReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const { 
    data: { users = [], pagination } = {}, 
    isLoading, 
    isError,
    error,
    refetch 
  } = useGetBlockStatusQuery({ 
    page, 
    limit, 
    search: debouncedSearchTerm, 
    block: blockFilter 
  });

  const [toggleBlockUser] = useToggleBlockUserMutation();

  const handleBlockToggle = (user) => {
    setCurrentUser(user);
    if (user.isBlocked) {
      confirmToggle(user.userId, false, '');
    } else {
      setShowReasonModal(true);
    }
  };

  const confirmToggle = async (userId, block, reason) => {
    setIsProcessing(true);
    try {
      await toggleBlockUser({ 
        userId, 
        block,
        reason 
      }).unwrap();
      await refetch();
    } catch (error) {
      console.error('Failed to update block status:', error);
    } finally {
      setIsProcessing(false);
      setShowReasonModal(false);
      setReason('');
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setBlockFilter(e.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setBlockFilter('');
    setPage(1);
  };

  // Check if filters are active
  const isFilterActive = searchTerm || blockFilter !== '';

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-red-50 border-l-4 border-red-500 p-4"
      >
        <div className="flex">
          <div className="flex-shrink-0">
            <FiAlertCircle className="h-5 w-5 text-red-500" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">
              Failed to load user block status: {error?.data?.message || error.message}
              <button 
                onClick={refetch}
                className="ml-2 text-blue-600 hover:text-blue-800 font-medium"
              >
                Retry
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-100">
      {/* Reason Modal */}
      <AnimatePresence>
        {showReasonModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div 
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium text-gray-900">Block Reason</h3>
                <button
                  onClick={() => {
                    setShowReasonModal(false);
                    setReason('');
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Please provide a reason for blocking {currentUser?.name}
              </p>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md mb-4 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                placeholder="Enter reason for blocking..."
                disabled={isProcessing}
                required
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowReasonModal(false);
                    setReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={isProcessing}
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmToggle(currentUser.userId, true, reason)}
                  className={`px-4 py-2 text-white rounded-md text-sm font-medium flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                    isProcessing || !reason.trim() ? 'bg-red-400' : 'bg-red-600 hover:bg-red-700'
                  }`}
                  disabled={isProcessing || !reason.trim()}
                >
                  {isProcessing ? (
                    <>
                      <FiRefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Processing...
                    </>
                  ) : (
                    'Confirm Block'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">User Block Management</h2>
            <p className="text-gray-500 text-sm mt-1">
              Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, pagination?.totalUsers || 0)} of {pagination?.totalUsers || 0} users
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={handleSearch}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                disabled={isProcessing}
              />
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiFilter className="h-5 w-5 text-gray-400" />
              </div>
              <select
                value={blockFilter}
                onChange={handleFilterChange}
                className="block w-full sm:w-40 pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm appearance-none"
                disabled={isProcessing}
              >
                <option value="">All Users</option>
                <option value="false">Active Only</option>
                <option value="true">Blocked Only</option>
              </select>
            </div>
            <button
              onClick={refetch}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center"
              disabled={isProcessing}
            >
              <FiRefreshCw className={`mr-2 h-4 w-4 ${isProcessing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Active Filters */}
        {isFilterActive && (
          <div className="mb-4 flex flex-wrap gap-2 items-center">
            <span className="text-sm text-gray-500">Filters:</span>
            {searchTerm && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Search: {searchTerm}
                <button 
                  onClick={() => setSearchTerm('')} 
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  <FiXCircle className="h-4 w-4" />
                </button>
              </span>
            )}
            {blockFilter === 'true' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Status: Blocked
                <button 
                  onClick={() => setBlockFilter('')} 
                  className="ml-1 text-red-600 hover:text-red-800"
                >
                  <FiXCircle className="h-4 w-4" />
                </button>
              </span>
            )}
            {blockFilter === 'false' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Status: Active
                <button 
                  onClick={() => setBlockFilter('')} 
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  <FiXCircle className="h-4 w-4" />
                </button>
              </span>
            )}
            <button 
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear all
            </button>
          </div>
        )}

        {users.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-12 text-center bg-gray-50 rounded-lg"
          >
            <div className="mx-auto h-16 w-16 text-gray-400">
              <FiUser className="mx-auto h-full w-full" />
            </div>
            <h3 className="mt-3 text-lg font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {isFilterActive ? 
                "No users match your current filters" : 
                "There are currently no users in the system"}
            </p>
            {isFilterActive && (
              <button 
                onClick={clearFilters}
                className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md"
                disabled={isProcessing}
              >
                Clear all filters
              </button>
            )}
          </motion.div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Block Reason
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.userId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center relative">
                            <span className="text-blue-600 font-medium">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                            <span className={`absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-white ${
                              user.isBlocked ? 'bg-red-500' : 'bg-green-500'
                            }`}></span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                            <div className="text-xs text-gray-400">{user.phone}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          user.isBlocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {user.isBlocked ? 'Blocked' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {user.reason || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => handleBlockToggle(user)}
                          className={`px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center min-w-[120px] transition-colors ${
                            user.isBlocked 
                              ? isProcessing && currentUser?.userId === user.userId 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                              : isProcessing && currentUser?.userId === user.userId 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                          }`}
                          disabled={isProcessing}
                        >
                          {isProcessing && currentUser?.userId === user.userId ? (
                            <>
                              <FiRefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                              {user.isBlocked ? 'Unblocking...' : 'Blocking...'}
                            </>
                          ) : (
                            <>
                              {user.isBlocked ? (
                                <>
                                  <FiCheck className="-ml-1 mr-2 h-4 w-4" />
                                  Unblock
                                </>
                              ) : (
                                <>
                                  <FiX className="-ml-1 mr-2 h-4 w-4" />
                                  Block
                                </>
                              )}
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Rows per page:</span>
                <select
                  value={limit}
                  onChange={handleLimitChange}
                  className="block pl-3 pr-8 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isProcessing}
                >
                  {[5, 10, 20, 50].map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1 || isProcessing}
                  className={`p-2 rounded-md ${
                    page === 1 || isProcessing
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <FiChevronLeft className="h-5 w-5" />
                </button>
                
                <span className="text-sm text-gray-700">
                  Page {page} of {pagination?.totalPages || 1}
                </span>
                
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= (pagination?.totalPages || 1) || isProcessing}
                  className={`p-2 rounded-md ${
                    page >= (pagination?.totalPages || 1) || isProcessing
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <FiChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserBlockManagement;